import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { ApplicationState } from '../../../../../store';
import {
  EDocumentType,
  EInvoiceStatus,
  IInvoiceData
} from '../../../../../store/ducks/invoices/types';
import {
  IUserPreference,
  EUserPreferenceType,
  EInvoiceColumn
} from '../../../../../store/ducks/user/types';
import {
  TableContainer,
  ButtonsTableContent,
  ContainerButtons
} from './styles';
import { formatCurrency } from '../../../../../utils/formatNumbers';
import DocumentsColumn from './DocumentsColumn';
import InvoiceStatusColumn from './InvoiceStatusColumn';
import SettingTable from './SettingTable';
import ExportData from './ExportData';
import moment from 'moment';
import Table from '../../../../../components/Table';

interface IDuplicatesTableDataSource {
  key: number;
  companyCode: string;
  nfNumber: string;
  orderNumber: string;
  documentType: EDocumentType;
  NFAcessKey: string;
  billingDate: string;
  dueDate: string;
  status: string;
  delayDate: string;
  totalValue: string;
  duplicateBilled: IInvoiceData;
  salesOrders: string;
}

interface IDuplicatesTableProps {
  filteredInvoices: IInvoiceData[];
  searchTerm: string;
  userOrderPreference?: IUserPreference;
}

const defaultOrderUserPreference: IUserPreference = {
  name: EUserPreferenceType.FINANCIALS,
  ordersItensFinancials: {
    idOrder: EInvoiceColumn.ORDER_NUMBER
  }
};

const DuplicatesTable: React.FC<IDuplicatesTableProps> = ({
  filteredInvoices,
  searchTerm
}) => {
  const [selectedInvoices, setSelectedInvoices] = useState<IInvoiceData[]>([]);

  const [duplicatesData, setDuplicatesData] = useState<
    IDuplicatesTableDataSource[]
  >([]);

  const [orderUserPreference, setOrderUserPreference] =
    useState<IUserPreference>(
      useSelector(
        (state: ApplicationState) =>
          state.user.preferences.find(
            (pref: IUserPreference) => pref.name === 'financials'
          ) as IUserPreference
      ) || defaultOrderUserPreference
    );

  const getColumns = () => {
    const getTextWithSearchTerm = (text: string): React.ReactNode => {
      if (text?.toUpperCase().includes(searchTerm.toUpperCase())) {
        const startPos = text.toUpperCase().indexOf(searchTerm.toUpperCase());
        return (
          <>
            {text.substring(0, startPos)}
            <strong>
              {text.substring(startPos, startPos + searchTerm.length)}
            </strong>
            {text.substring(
              startPos + searchTerm.length,
              startPos + text.length
            )}
          </>
        );
      } else {
        return <>{text}</>;
      }
    };

    const canDownloadDocument = (document: EDocumentType): boolean => {
      return document !== EDocumentType.NOTA_JUROS;
    };

    const columns = [
      {
        title: 'Nº da NF',
        dataIndex: 'nfNumber',
        sorter: (
          a: IDuplicatesTableDataSource,
          b: IDuplicatesTableDataSource
        ) =>
          Number(a.nfNumber.replace('-', '')) -
          Number(b.nfNumber.replace('-', '')),
        render: (text: string) => <>{getTextWithSearchTerm(text)}</>
      },
      {
        title: 'Nº do pedido',
        dataIndex: 'orderNumber',
        sorter: (
          a: IDuplicatesTableDataSource,
          b: IDuplicatesTableDataSource
        ) =>
          Number(a.orderNumber.replace(/[^\d]+/g, '')) -
          Number(b.orderNumber.replace(/[^\d]+/g, '')),
        render: (text: string) => <>{getTextWithSearchTerm(text)}</>
      },
      {
        title: 'Ordem de venda',
        dataIndex: 'salesOrders',
        sorter: (
          a: IDuplicatesTableDataSource,
          b: IDuplicatesTableDataSource
        ) =>
          Number(a.salesOrders.replace('-', '')) -
          Number(b.salesOrders.replace('-', '')),
        render: (text: string) => <>{getTextWithSearchTerm(text)}</>
      },
      {
        title: 'Tipo',
        dataIndex: 'documentType',
        sorter: (
          a: IDuplicatesTableDataSource,
          b: IDuplicatesTableDataSource
        ) => a.documentType.length - b.documentType.length,
        render: (documentType: EDocumentType) => (
          <>{getTextWithSearchTerm(documentType)}</>
        )
      },
      {
        title: 'Faturamento',
        dataIndex: 'billingDate',
        sorter: (
          a: IDuplicatesTableDataSource,
          b: IDuplicatesTableDataSource
        ) =>
          moment(a.billingDate, 'YYYY-MM-DD').toDate().getTime() -
          moment(b.billingDate, 'YYYY-MM-DD').toDate().getTime(),
        render: (billingDate: string) => (
          <>
            {getTextWithSearchTerm(
              moment(billingDate, 'YYYY-MM-DD').format('DD/MM/YYYY')
            )}
          </>
        )
      },
      {
        title: 'Vencimento',
        dataIndex: 'dueDate',
        sorter: (
          a: IDuplicatesTableDataSource,
          b: IDuplicatesTableDataSource
        ) =>
          moment(a.dueDate, 'YYYY-MM-DD').toDate().getTime() -
          moment(b.dueDate, 'YYYY-MM-DD').toDate().getTime(),
        render: (dueDate: string) => (
          <>
            {dueDate
              ? getTextWithSearchTerm(
                  moment(dueDate, 'YYYY-MM-DD').format('DD/MM/YYYY')
                )
              : ' - '}
          </>
        )
      },
      {
        title: 'Status',
        dataIndex: 'status',
        sorter: (
          a: IDuplicatesTableDataSource,
          b: IDuplicatesTableDataSource
        ) => a.status.length - b.status.length,
        render: (status: EInvoiceStatus) => (
          <InvoiceStatusColumn status={status} />
        )
      },
      {
        title: 'Valor total',
        dataIndex: 'totalValue',
        sorter: (
          a: IDuplicatesTableDataSource,
          b: IDuplicatesTableDataSource
        ) =>
          +a.totalValue.toString().replace(',', '') -
          +b.totalValue.toString().replace(',', ''),
        render: (totalValue: string) => (
          <>
            {totalValue === 'Indisponível'
              ? totalValue
              : getTextWithSearchTerm(
                  'R$ ' +
                    formatCurrency(totalValue.toString().replace(/,/g, ''))
                )}
          </>
        )
      },
      {
        title: <span className="title">Documentos</span>,
        dataIndex: 'duplicateBilled',
        render: (invoice: IInvoiceData) =>
          canDownloadDocument(invoice.documentType) ? (
            <DocumentsColumn invoice={invoice} />
          ) : (
            <>-</>
          )
      }
    ];

    const hideColumns: string[] = [];

    if (
      orderUserPreference &&
      orderUserPreference?.ordersItensFinancials?.idOrder ===
        EInvoiceColumn.SALES_ORDERS
    ) {
      hideColumns.push('orderNumber'); // Nº Pedido Cliente
    } else {
      hideColumns.push('salesOrders'); // OV Gerdau
    }

    return columns.filter((col) => !hideColumns.includes(col.dataIndex));
  };

  const rowSelection = {
    onChange: (_: React.Key[], selectedRows: IDuplicatesTableDataSource[]) => {
      const selectedItens = filteredInvoices.filter(
        (item: IInvoiceData, index: number) => {
          const findSelectedItem = selectedRows.find(
            (selectedItem: IDuplicatesTableDataSource) =>
              selectedItem.key === index
          );
          item.tableId = index;
          return findSelectedItem;
        }
      );

      setSelectedInvoices(selectedItens);
    },

    getCheckboxProps: (record: IDuplicatesTableDataSource) => {
      const invoice = filteredInvoices.find(
        (invoice: IInvoiceData, index: number) =>
          invoice.danfeNumber === record.nfNumber && record.key === index
      );
      return { ...(invoice && { disabled: true, checked: true }) };
    }
  };

  useEffect(() => {
    setDuplicatesData(
      convertFilteredInvoicesToIDuplicatesTableDataSource(filteredInvoices)
    );
  }, [filteredInvoices]);

  const convertFilteredInvoicesToIDuplicatesTableDataSource = (
    dataInvoices: IInvoiceData[]
  ) => {
    return dataInvoices.map((invoice, key) => ({
      key,
      companyCode: invoice.companyCode,
      nfNumber: invoice.invoiceNumber,
      orderNumber: invoice.purchaseOrder ? invoice.purchaseOrder : ' - ',
      documentType: invoice.documentType,
      NFAcessKey: invoice.nfeAccessKey,
      billingDate: invoice.invoiceDate,
      dueDate: invoice.dueDate,
      status: invoice.status,
      delayDate: invoice.delayedInvoiceDays,
      totalValue: invoice.openInvoiceAmount,
      duplicateBilled: invoice,
      salesOrders: invoice.salesOrders ? invoice.salesOrders : ' - '
    }));
  };

  return (
    <>
      {filteredInvoices.length > 0 && (
        <ContainerButtons>
          <ButtonsTableContent>
            <SettingTable
              orderUserPreference={orderUserPreference}
              setOrderUserPreference={setOrderUserPreference}
            />
            <ExportData
              invoices={
                selectedInvoices.length > 0
                  ? selectedInvoices
                  : filteredInvoices
              }
            />
          </ButtonsTableContent>
        </ContainerButtons>
      )}

      <TableContainer>
        <Table
          columns={getColumns()}
          data={duplicatesData}
          rowSelection={rowSelection}
          hasPagination
        />
      </TableContainer>
    </>
  );
};

export default DuplicatesTable;
